import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './SubscriptionVerify.css';

const SubscriptionVerify = () => {
  const { reference } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');

  const verifyPayment = useCallback(async () => {
    try {
      setStatus('verifying');

      // Call backend to verify payment
      const response = await fetch(`/api/checkout/verify-subscription/${reference}`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubscription(data.subscription);

        // Check if payment was successful
        if (data.subscription.status === 'ACTIVE' && data.subscription.paymentStatus === 'PAID') {
          setStatus('success');

          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/overview', { replace: true });
          }, 3000);
        } else if (data.subscription.status === 'PENDING') {
          // Payment is still pending - wait for webhook
          setStatus('pending');

          // Poll for status update every 3 seconds for up to 30 seconds
          let attempts = 0;
          const pollInterval = setInterval(async () => {
            attempts++;

            if (attempts >= 10) {
              clearInterval(pollInterval);
              setStatus('timeout');
              return;
            }

            const pollResponse = await fetch(`/api/checkout/verify-subscription/${reference}`, {
              credentials: 'include'
            });

            const pollData = await pollResponse.json();

            if (pollData.success && pollData.subscription.status === 'ACTIVE') {
              clearInterval(pollInterval);
              setSubscription(pollData.subscription);
              setStatus('success');

              setTimeout(() => {
                navigate('/overview', { replace: true });
              }, 3000);
            }
          }, 3000);
        } else {
          setStatus('error');
          setError('Payment was not successful. Please try again.');
        }
      } else {
        setStatus('error');
        setError(data.error || 'Failed to verify payment');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setStatus('error');
      setError('An error occurred while verifying your payment. Please contact support.');
    }
  }, [reference, navigate]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
      case 'pending':
        return (
          <div className="verify-content">
            <div className="loading-spinner"></div>
            <h2>Verifying Your Payment</h2>
            <p>Please wait while we confirm your subscription upgrade...</p>
            <div className="payment-reference">
              <small>Reference: {reference}</small>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="verify-content success">
            <div className="success-icon">✓</div>
            <h2>Payment Successful!</h2>
            <p>Your subscription has been upgraded successfully.</p>
            {subscription && (
              <div className="subscription-details">
                <div className="detail-item">
                  <span className="detail-label">Plan:</span>
                  <span className="detail-value">{subscription.tier}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Billing Period:</span>
                  <span className="detail-value">{subscription.period === 'YEARLY' ? 'Yearly' : 'Monthly'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Amount:</span>
                  <span className="detail-value">
                    {new Intl.NumberFormat('en-NG', {
                      style: 'currency',
                      currency: subscription.currency || 'NGN'
                    }).format(subscription.amount)}
                  </span>
                </div>
              </div>
            )}
            <p className="redirect-message">Taking you to your overview in 3 seconds…</p>
            <button onClick={() => navigate('/overview')} className="continue-button">
              Continue
            </button>
          </div>
        );

      case 'timeout':
        return (
          <div className="verify-content timeout">
            <div className="timeout-icon">⏱️</div>
            <h2>Payment Processing</h2>
            <p>Your payment is being processed. This may take a few minutes.</p>
            <p>We'll send you an email confirmation once your subscription is activated.</p>
            <button onClick={() => navigate('/overview')} className="continue-button">
              Go to Overview
            </button>
            <button onClick={verifyPayment} className="retry-button">
              Check Status Again
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="verify-content error">
            <div className="error-icon">✕</div>
            <h2>Payment Verification Failed</h2>
            <p className="error-message">{error}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/overview')} className="continue-button">
                Return to Overview
              </button>
              <button onClick={verifyPayment} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="subscription-verify-container">
      <div className="verify-card">
        {renderContent()}
      </div>
    </div>
  );
};

export default SubscriptionVerify;
